/**
 * The main front-end entry point for the courses app.
 */

import { useRouteContext, useRouteDef, useRouteParams } from '@components/router';
import { AcctBanner, MaintenanceBanner } from '@course/components/banners';
import { ChatWidget } from '@course/components/chat-widget';
import { globalConfig } from 'client/lib/auth';
import { useEffect } from 'preact/hooks';
import { initSentry } from 'client/lib/sentry';
import dayjs from 'dayjs';
import type { Locales } from 'server/types';
import { courseStyleCache } from '@course/components/courses-app-router';
import { isRuzcalUrl } from 'shared/urls';

const dayjsLocales: Record<Locales, any> = {
  en: () => import('dayjs/locale/en'),
  es: () => import('dayjs/locale/es'),
};

/**
 * Render the course CSS link tag if appropriate.
 */
function CourseCSS() {
  const params = useRouteParams();
  const def = useRouteDef();

  const themedCourseId =
    params.courseId && (def.isPublic || def.authLevel === 'student') ? params.courseId : '';

  return themedCourseId && <style>{courseStyleCache[themedCourseId]}</style>;
}

/**
 * Initialize all of our various trackers, and return the config.
 */
function useLoadConfiguration() {
  const configuration = globalConfig();

  useEffect(() => {
    if (!configuration) {
      return;
    }
    if (configuration) {
      initSentry(configuration);
    }

    // Load the appropriate locale for the current route.
    // if the tenant has a non-english locale.
    if (configuration.tenant.locale !== 'en') {
      const newLocale = configuration.tenant.locale || 'en';

      if (dayjs.locale() !== newLocale) {
        dayjsLocales[newLocale]().then(() => {
          dayjs.locale(newLocale);
        });
      }
    }
  }, [configuration]);

  return configuration;
}

/**
 * Render the root component, and wire up the application context.
 */
export function CoursesApp() {
  const route = useRouteContext();

  useLoadConfiguration();

  return (
    <>
      <CourseCSS />
      <MaintenanceBanner />
      <AcctBanner />
      {!isRuzcalUrl(route.url) && <ChatWidget />}
    </>
  );
}
